'use client';

/**
 * 分配上传任务弹窗
 */

import { useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  MeetingUserSelector,
  type MeetingUser,
} from '@/features/meeting-attendance/components/MeetingUserSelector';
import { useTranslation } from '@/hooks/useTranslation';
import { uploadTaskApi } from '@/services/api/upload-task';
import { ApiClientError } from '@/lib/api-client';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agendaItemId: string;
  onAssigned: () => void;
}

export function AssignTaskDialog({ open, onOpenChange, agendaItemId, onAssigned }: Props) {
  const { t } = useTranslation();
  const tag = t.meetingAttendance.agenda.uploadTask;
  const [users, setUsers] = useState<MeetingUser[]>([]);
  const [dueAt, setDueAt] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setUsers([]);
    setDueAt('');
  };

  const handleSubmit = async () => {
    if (users.length === 0) return;
    setSubmitting(true);
    try {
      const resp = await uploadTaskApi.assignUploadTasks(agendaItemId, {
        assigneeUserIds: users.map((u) => u.id),
        dueAt: dueAt ? new Date(dueAt).toISOString() : undefined,
      });
      const createdCount = resp.created.length;
      const skippedCount = resp.skippedExistingUserIds?.length || 0;
      if (createdCount > 0) {
        toast.success(tag.assignSuccess.replace('{count}', String(createdCount)));
      }
      if (skippedCount > 0) {
        toast.info(tag.skippedExisting.replace('{count}', String(skippedCount)));
      }
      reset();
      onAssigned();
      onOpenChange(false);
    } catch (err) {
      const code = err instanceof ApiClientError ? err.code : undefined;
      toast.error(code === 'MEETING_ATTENDANCE_011' ? code : tag.assignFailed);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{tag.assignTitle}</DialogTitle>
          <DialogDescription>{tag.assignDescription}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-sm">{tag.assigneesLabel}</Label>
            <div className="mt-1">
              <MeetingUserSelector selectedUsers={users} onUsersChange={setUsers} />
            </div>
          </div>
          <div>
            <Label className="text-sm">{tag.dueAtLabel}</Label>
            <Input
              type="datetime-local"
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
              placeholder={tag.dueAtPlaceholder}
              className="mt-1"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t.meetingAttendance.common.back}
          </Button>
          <Button
            disabled={users.length === 0 || submitting}
            onClick={handleSubmit}
          >
            {tag.assignButton}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
